import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Logger, LoggingService } from 'ionic-logging-service';
import { IDelegatedConsent } from '@app/services/delegated-consents/models/delegated-consent.model';
import { SanitizationService } from '@app/services/sanitization/sanitization.service';

// Raw API shapes. Every field is optional: this is untrusted wire data, narrowed for shape in
// deserializeConsent() — and, for the two strings that reach the template, sanitized there too.
// Rels carry the "px:" curie prefix on the wire; the bare names are accepted too so the curie
// is not load-bearing.
interface IDelegatedConsentResponseItem {
  kind?: string;
  directory?: string;
  directoryName?: string;
  consentRequired?: boolean;
  _links?: {
    'px:consent'?: { href?: string };
    consent?: { href?: string };
  };
}

interface IDelegatedConsentsResponse {
  _embedded?: {
    'px:delegatedConsents'?: IDelegatedConsentResponseItem[];
    delegatedConsents?: IDelegatedConsentResponseItem[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class DelegatedConsentsService {

  private logger: Logger;

  constructor(
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    private sanitizationService: SanitizationService
  ) {
    this.logger = loggingService.getLogger('[DelegatedConsentsService]');
    const methodName = 'ctor';
    this.logger.entry(methodName);
  }

  /**
   * Lists the delegated cloud-storage consents the signed-in user can grant. Only
   * directories carrying the tenant's admin consent are returned, so an empty list means
   * there is nothing for the user to authorize.
   *
   * Not routed through ErrorService: this is a capability probe rendered as an optional
   * page section, and a tenant without delegated destinations (or an older backend) must
   * degrade to no section rather than an error modal.
   *
   * Distinguishes "nothing to authorize" from "could not ask", which the endpoint itself does
   * not: `[]` means the backend answered and there is nothing, `null` means the request failed.
   * Callers refreshing an already-rendered list must skip `null` rather than render it as empty —
   * `GoPage` refetches on `platform.resume`, and collapsing a transient failure to `[]` there
   * erased a section that had been fine until the app was foregrounded a second too early.
   *
   * @param tenantSelfLink - HATEOAS self link of the current tenant
   * @returns The consents, `[]` when there are none, or `null` when the request failed
   */
  public getDelegatedConsents(tenantSelfLink: string): Observable<IDelegatedConsent[] | null> {
    const methodName = 'getDelegatedConsents()';
    const validatedLink = this.sanitizationService.validateApiUrl(tenantSelfLink);
    if (!validatedLink) {
      this.logger.info(methodName + ' No valid tenant self link');
      // null, not []: no request was made, so the backend has not said the list is empty.
      return of(null);
    }

    return this.httpClient.get<IDelegatedConsentsResponse>(validatedLink + '/delegated-consents').pipe(
      take(1),
      map((response: IDelegatedConsentsResponse) => this.deserializeConsents(response)),
      catchError((error) => {
        this.logger.info(methodName + ' unavailable - status: ' + (error?.status ?? 'unknown'));
        return of(null);
      })
    );
  }

  // The endpoint returns a HATEOAS CollectionModel. Spring omits _embedded entirely when the
  // collection is empty, so its absence is the normal "nothing to authorize" case.
  private deserializeConsents(response: IDelegatedConsentsResponse): IDelegatedConsent[] {
    const embedded = response?._embedded;
    const items = embedded?.['px:delegatedConsents'] ?? embedded?.delegatedConsents;
    if (!Array.isArray(items)) {
      return [];
    }
    return items.map((item: IDelegatedConsentResponseItem) => this.deserializeConsent(item));
  }

  // kind and directoryName both reach the template — as the row label's fallback and as the
  // directory name — so they go through sanitizeDisplayText() like every other displayed string
  // deserialized from the API (see TenantService.deserializeTenant). Interpolation escapes, so
  // this is consistency rather than an XSS fix.
  //
  // The wire item's directory is deliberately not carried onto the model: nothing reads it. The
  // value the app acts on is lifted out of consentUrl's query string by
  // GoPage.authorizeDelegatedConsent() and validated by ActivateDelegatedPage.
  private deserializeConsent(item: IDelegatedConsentResponseItem): IDelegatedConsent {
    return {
      // Sent as the DelegatedGrantKind enum name (ONEDRIVE / SHAREPOINT).
      kind: typeof item?.kind === 'string'
        ? this.sanitizationService.sanitizeDisplayText(item.kind).toLowerCase()
        : '',
      directoryName: this.sanitizationService.sanitizeDisplayText(item?.directoryName ?? ''),
      // !== false, not === true. Every other narrowing here degrades to blank or hidden; this one
      // decides which of two opposite claims the row makes. Defaulting an absent value to false
      // prints "Access granted" and withholds the button, so the user is told they have access
      // they may not have, with no way to act. Defaulting to true costs at most a redundant round
      // trip through a consent they already hold.
      consentRequired: item?.consentRequired !== false,
      consentUrl: item?._links?.['px:consent']?.href ?? item?._links?.consent?.href ?? null
    };
  }
}
